'use client';

import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["occurrence", "variant", "style", "className"];
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { createSelector, useStore } from '@base-ui/utils/store';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { CalendarGrid } from '@mui/x-scheduler-internals/calendar-grid';
import { schedulerEventSelectors, schedulerResourceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { eventCalendarViewSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { isOccurrenceAllDayOrMultipleDay } from "../../../utils/event-utils.mjs";
import { EventDragPreview } from "../../event-drag-preview/index.mjs";
import { useFormatTime } from "../../../hooks/useFormatTime.mjs";
import { getPaletteVariants } from "../../../utils/tokens.mjs";
import { useEventCalendarStyledContext } from "../../../../event-calendar/EventCalendarStyledContext.mjs";
import { eventCalendarClasses } from "../../../../event-calendar/eventCalendarClasses.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ARROW_DEPTH = 8; // px - depth of the chevron point
const LEFT_ARROW_CLIP = `polygon(${ARROW_DEPTH}px 0, 100% 0, 100% 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;
const RIGHT_ARROW_CLIP = `polygon(0 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, 0 100%)`;
const BOTH_ARROWS_CLIP = `polygon(${ARROW_DEPTH}px 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;
const DayGridEventBaseStyles = theme => ({
  containerType: 'inline-size',
  borderRadius: theme.shape.borderRadius * 0.75,
  minWidth: 18,
  height: 18,
  cursor: 'pointer',
  position: 'relative',
  zIndex: 1,
  gridRow: 'var(--grid-row)',
  gridColumn: 1,
  padding: `0 ${theme.spacing(0.5)}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: `calc(var(--grid-column-span) * 100% + (var(--grid-column-span) - 1) * (2 * ${theme.spacing(0.5)} + 1px))`,
  '&[data-dragging], &[data-resizing]': {
    opacity: 0.5
  },
  variants: getPaletteVariants(theme)
});
const DayGridEventRoot = styled(CalendarGrid.DayEvent, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEvent'
})(({
  theme
}) => _extends({}, DayGridEventBaseStyles(theme), {
  '&:focus-visible': {
    outline: '2px solid var(--event-surface-accent)',
    outlineOffset: 1
  },
  '&[data-variant="filled"]': {
    backgroundColor: 'var(--event-surface-bold)',
    '&:active': {},
    '&:hover': {
      backgroundColor: 'var(--event-surface-bold-hover)'
    },
    '&[data-editing]': {
      backgroundColor: 'var(--event-surface-selected)',
      '&:hover': {
        backgroundColor: 'var(--event-surface-selected-hover)'
      }
    },
    [`& .${eventCalendarClasses.dayGridEventRecurringIcon}`]: {
      color: 'var(--event-on-surface-bold)'
    },
    '&[data-starting-before-edge]': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      clipPath: LEFT_ARROW_CLIP,
      paddingLeft: ARROW_DEPTH + 8
    },
    '&[data-ending-after-edge]': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      clipPath: RIGHT_ARROW_CLIP,
      paddingRight: ARROW_DEPTH + 8
    },
    '&[data-starting-before-edge][data-ending-after-edge]': {
      clipPath: BOTH_ARROWS_CLIP
    },
    '&[data-starting-before-edge]:focus-visible, &[data-ending-after-edge]:focus-visible': {
      clipPath: 'none',
      borderRadius: theme.shape.borderRadius * 0.75
    }
  },
  '&[data-variant="invisible"]': {
    width: '100%',
    visibility: 'hidden',
    height: 18
  },
  '&[data-variant="compact"]': {
    height: 'fit-content',
    '&:active': {},
    '&:hover': {
      backgroundColor: (theme.vars || theme).palette.action.hover
    },
    '&[data-editing]': {
      backgroundColor: 'var(--event-surface-subtle)',
      '&:hover': {
        backgroundColor: 'var(--event-surface-subtle-hover)'
      }
    }
  }
}));
const DayGridEventPlaceholder = styled(CalendarGrid.DayEventPlaceholder, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventPlaceholder'
})(({
  theme
}) => _extends({}, DayGridEventBaseStyles(theme), {
  minHeight: 18,
  zIndex: 2,
  backgroundColor: 'var(--event-surface-subtle-hover)',
  color: 'var(--event-on-surface-subtle-primary)',
  border: `1px dashed var(--event-on-surface-subtle-secondary)`
}));
const DayGridEventTitle = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventTitle'
})(({
  theme
}) => ({
  margin: 0,
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: 'var(--EventCalendar-fontSize-eventTitle, 0.75rem)',
  lineHeight: 1.43,
  flexGrow: 1,
  '[data-variant="filled"] &': {
    color: 'var(--event-on-surface-bold)'
  },
  '[data-variant="compact"] &': {
    color: (theme.vars || theme).palette.text.primary
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-subtle-primary)'
  }
}));
const DayGridEventTime = styled('time', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventTime'
})(({
  theme
}) => ({
  display: 'inline-block',
  color: 'var(--event-on-surface-subtle-secondary)',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.43,
  whiteSpace: 'nowrap',
  paddingInlineEnd: theme.spacing(0.5),
  '@container (width < 300px)': {
    paddingInlineEnd: 0,
    display: 'inline',
    '& > span:last-of-type': {
      display: 'none'
    }
  },
  '[data-variant="compact"] &': {
    color: (theme.vars || theme).palette.text.secondary
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-subtle-secondary)'
  }
}));
const DayGridEventRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventRecurringIcon'
})(({
  theme
}) => ({
  color: (theme.vars || theme).palette.text.primary,
  fontSize: '1rem',
  justifySelf: 'flex-end',
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  },
  '[data-variant="compact"][data-editing] &': {
    color: 'var(--event-on-surface-bold)'
  }
}));
const DayGridEventResizeHandler = styled(CalendarGrid.DayEventResizeHandler, {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventResizeHandler'
})({
  position: 'absolute',
  width: 4,
  top: 0,
  bottom: 0,
  zIndex: 3,
  cursor: 'ew-resize',
  opacity: 0,
  '*:hover > &': {
    opacity: 1
  },
  '&[data-start]': {
    left: 0
  },
  '&[data-end]': {
    right: 0
  }
});
const DayGridEventCardWrapper = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventCardWrapper'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexGrow: 1,
  '@container (width < 300px)': {
    gap: theme.spacing(0.5),
    [`& .${eventCalendarClasses.dayGridEventTitle}`]: {
      marginInlineStart: theme.spacing(0.5),
      paddingInlineEnd: theme.spacing(1.5)
    }
  }
}));
const DayGridEventCardContent = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventCardContent'
})({
  margin: 0,
  height: 20,
  lineHeight: '20px'
});
const EventColorIndicator = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'EventColorIndicator'
})({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: 'var(--event-main)',
  marginTop: 2
});
const DayGridEventLinesClamp = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'DayGridEventLinesClamp'
})({
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  flexGrow: 1
});
const isResizableSelector = createSelector((state, side, occurrence) => {
  if (!schedulerEventSelectors.isResizable(state, occurrence.id, side)) {
    return false;
  }
  const view = eventCalendarViewSelectors.view(state);

  // There is only one day cell in the day view
  if (view === 'day') {
    return false;
  }

  // In month view, only multi-day and all-day events can be resized
  if (view === 'month') {
    return isOccurrenceAllDayOrMultipleDay(occurrence, state.adapter);
  }
  return true;
});
export const DayGridEvent = /*#__PURE__*/React.forwardRef(function DayGridEvent(props, forwardedRef) {
  const {
      occurrence,
      variant,
      style: styleProp,
      className
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);

  // Context hooks
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  // Selector hooks
  const isDraggable = useStore(store, schedulerEventSelectors.isDraggable, occurrence.id);
  const isStartResizable = useStore(store, isResizableSelector, 'start', occurrence);
  const isEndResizable = useStore(store, isResizableSelector, 'end', occurrence);
  const isRecurring = useStore(store, schedulerEventSelectors.isRecurring, occurrence.id);
  const resource = useStore(store, schedulerResourceSelectors.processedResource, occurrence.resource);
  const color = useStore(store, schedulerEventSelectors.color, occurrence.id);

  // Feature hooks
  const formatTime = useFormatTime();
  const content = React.useMemo(() => {
    switch (variant) {
      case 'invisible':
        {
          return null;
        }
      case 'filled':
      case 'placeholder':
        return /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(DayGridEventLinesClamp, {
            className: classes.dayGridEventLinesClamp,
            style: {
              '--number-of-lines': 1
            },
            children: /*#__PURE__*/_jsx(DayGridEventTitle, {
              className: classes.dayGridEventTitle,
              children: occurrence.title
            })
          }), isRecurring && /*#__PURE__*/_jsx(DayGridEventRecurringIcon, {
            className: classes.dayGridEventRecurringIcon,
            "aria-hidden": "true",
            fontSize: "small"
          })]
        });
      case 'compact':
        return /*#__PURE__*/_jsxs(DayGridEventCardWrapper, {
          className: classes.dayGridEventCardWrapper,
          children: [/*#__PURE__*/_jsx(EventColorIndicator, {
            className: classes.eventColorIndicator,
            role: "img",
            "aria-label": resource?.title ? localeText.resourceAriaLabel(resource.title) : localeText.noResourceAriaLabel
          }), /*#__PURE__*/_jsx(DayGridEventCardContent, {
            className: classes.dayGridEventCardContent,
            children: /*#__PURE__*/_jsxs(DayGridEventLinesClamp, {
              className: classes.dayGridEventLinesClamp,
              style: {
                '--number-of-lines': 1
              },
              children: [/*#__PURE__*/_jsxs(DayGridEventTime, {
                className: classes.dayGridEventTime,
                children: [/*#__PURE__*/_jsx("span", {
                  children: formatTime(occurrence.displayTimezone.start.value)
                }), /*#__PURE__*/_jsxs("span", {
                  children: [" - ", formatTime(occurrence.displayTimezone.end.value)]
                })]
              }), /*#__PURE__*/_jsx(DayGridEventTitle, {
                className: classes.dayGridEventTitle,
                as: "span",
                children: occurrence.title
              })]
            })
          }), isRecurring && /*#__PURE__*/_jsx(DayGridEventRecurringIcon, {
            className: classes.dayGridEventRecurringIcon,
            "aria-hidden": "true",
            fontSize: "small"
          })]
        });
      default:
        throw new Error(process.env.NODE_ENV !== "production" ? 'MUI X Scheduler: Unsupported variant provided to DayGridEvent component. ' + 'The DayGridEvent component only supports specific variant values. ' + 'Check the component documentation for supported variants.' : _formatErrorMessage(208));
    }
  }, [variant, occurrence.title, occurrence.displayTimezone.start.value, occurrence.displayTimezone.end.value, isRecurring, resource?.title, localeText, formatTime, classes]);
  const sharedProps = _extends({
    start: occurrence.displayTimezone.start,
    end: occurrence.displayTimezone.end,
    ref: forwardedRef,
    'data-variant': variant,
    'data-palette': color,
    style: _extends({
      '--grid-row': occurrence.position.index,
      '--grid-column-span': occurrence.position.daySpan
    }, styleProp)
  }, other, {
    className: clsx(className, occurrence.className)
  });
  if (variant === 'placeholder') {
    return /*#__PURE__*/_jsx(DayGridEventPlaceholder, _extends({
      "aria-hidden": true
    }, sharedProps, {
      className: clsx(classes.dayGridEventPlaceholder, sharedProps.className),
      children: content
    }));
  }
  return /*#__PURE__*/_jsxs(DayGridEventRoot, _extends({
    eventId: occurrence.id,
    occurrenceKey: occurrence.key,
    isDraggable: isDraggable,
    renderDragPreview: parameters => /*#__PURE__*/_jsx(EventDragPreview, _extends({}, parameters)),
    "aria-hidden": variant === 'invisible'
  }, sharedProps, {
    className: clsx(classes.dayGridEvent, sharedProps.className),
    children: [isStartResizable && /*#__PURE__*/_jsx(DayGridEventResizeHandler, {
      className: classes.dayGridEventResizeHandler,
      side: "start"
    }), content, isEndResizable && /*#__PURE__*/_jsx(DayGridEventResizeHandler, {
      className: classes.dayGridEventResizeHandler,
      side: "end"
    })]
  }));
});
if (process.env.NODE_ENV !== "production") DayGridEvent.displayName = "DayGridEvent";